import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DerivativeActionPermissionsService } from '@features/dashboard/control/derivative-management/derivatives/services/derivative-action-permissions.service';
import {
  DerivativeOperationStrategyType,
  OperationDirectionType,
} from '@src/app/core/enums/derivatives';
import {
  DerivativeMtmDailyItemDto,
  DerivativeOperationDto,
} from '@src/app/core/interfaces/derivatives';
import { OperationComplianceEvaluateRequest } from '@src/app/core/interfaces/risk-monitoring/requests/operation-compliance-evaluate.request';
import { MandateViolation } from '@src/app/core/interfaces/risk-monitoring/responses/mandate-compliance.response';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import { DerivativesHttpService } from '@src/app/core/services/http/derivatives/derivatives-http.service';
import { DerivativeMtmDailyHttpService } from '@src/app/core/services/http/derivatives/mtm-daily/derivative-mtm-daily-http.service';
import { RiskMonitoringHttpService } from '@src/app/core/services/http/risk-monitoring/risk-monitoring-http.service';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { MandateComplianceAlertComponent } from '@src/app/shared/components/mandate-compliance-alert/mandate-compliance-alert.component';
import { SortEvent } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { finalize, forkJoin } from 'rxjs';

@Component({
  selector: 'app-modal-mtm-daily',
  imports: [CommonModule, SharedModule, MandateComplianceAlertComponent],
  templateUrl: './modal-mtm-daily.component.html',
  styleUrls: ['./modal-mtm-daily.component.scss'],
})
export class ModalMtmDailyComponent {
  @ViewChild('dialog') dialog!: DialogComponent;

  dialogKey = 'modal-mtm-daily-' + Math.random().toString(36).substring(2, 15);

  data: DerivativeMtmDailyItemDto[] = [];
  sortedData: DerivativeMtmDailyItemDto[] = [];
  operation: DerivativeOperationDto | null = null;
  isLoading = false;
  isGenerating = false;
  /** Card 3902: "Gerar MtM Diário" só com `DerivativeCalculator.CREATE`, a claim do endpoint. */
  readonly actionPermissions = inject(DerivativeActionPermissionsService);
  derivativeOperationId: string | null = null;

  /** Violações de mandato retornadas pela avaliação em tempo real (dry-run, 1x ao abrir). */
  mandateViolations: MandateViolation[] = [];

  /** Indica se um mandato ativo foi resolvido para a empresa do derivativo. */
  mandateResolved: boolean | null = null;

  private readonly baseColumnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({
      field: 'dataBase',
      header: 'Data Base',
      format: 'date',
      sortField: 'dataBase',
    }),
    new ColumnDefinition({
      field: 'mtmData.mtmDetails.dataReferencia',
      header: 'Data Referência',
      template: 'dataReferencia',
      sortField: 'mtmData.mtmDetails.dataReferencia',
    }),
    new ColumnDefinition({
      field: 'mtmData.taxaMercadoRound',
      header: 'Taxa Mercado',
      template: 'taxaMercado',
      sortField: 'mtmData.taxaMercadoRound',
      format: 'currency:4',
    }),
    new ColumnDefinition({
      field: 'mtmData.valorMercadoRound',
      header: 'Valor Mercado',
      template: 'valorMercado',
      sortField: 'mtmData.valorMercadoRound',
      format: 'currency:2',
    }),
    new ColumnDefinition({
      field: 'mtmData.saldoMTMRound',
      header: 'Saldo MtM',
      template: 'saldoMTM',
      sortField: 'mtmData.saldoMTMRound',
      format: 'currency:2',
    }),
  ];

  get columnDefinitions(): ColumnDefinition[] {
    if (!this.isSwapWithCap) {
      return this.baseColumnDefinitions;
    }

    return [
      ...this.baseColumnDefinitions,
      new ColumnDefinition({
        field: 'mtmAdjustedBalance',
        header: 'Saldo MtM Ajustado',
        template: 'mtmAdjustedBalance',
        sortField: 'mtmAdjustedBalance',
        format: 'currency:2',
      }),
    ];
  }

  /**
   * Verifica se é uma operação do tipo Call Spread
   */
  get isCallSpread(): boolean {
    return this.operation?.operationStrategyType === DerivativeOperationStrategyType.Callspread;
  }

  /**
   * Verifica se é uma operação do tipo Call Vanilla
   */
  get isCallVanilla(): boolean {
    return this.operation?.operationStrategyType === DerivativeOperationStrategyType.CallVanilla;
  }

  get isPutSpread(): boolean {
    return this.operation?.operationStrategyType === DerivativeOperationStrategyType.PutSpread;
  }

  get isPutVanilla(): boolean {
    return this.operation?.operationStrategyType === DerivativeOperationStrategyType.PutVanilla;
  }

  get isSwapWithCap(): boolean {
    return this.operation?.operationStrategyType === DerivativeOperationStrategyType.SwapWithCap;
  }

  /**
   * Verifica se é uma operação do tipo NDF (com ou sem CAP)
   */
  get isNdf(): boolean {
    return !this.isCallSpread && !this.isCallVanilla && !this.isPutSpread && !this.isPutVanilla;
  }

  /**
   * Verifica se a operação NDF tem CAP
   */
  get hasCap(): boolean {
    const cap = this.operation?.capValue;
    return cap !== null && cap !== undefined && cap !== 0;
  }

  /**
   * Retorna os dados fixos da operação
   */
  get operationInfo(): {
    // Campos comuns
    notional: number;
    dataOperacao: string | null;
    dataVencimento: string | null;
    spot: number;
    // Campos NDF
    forward: number;
    cap: number;
    // Campos Call Spread
    strikeCallComprada: number | null;
    strikeCallVendida: number | null;
    // Campos Call Vanilla
    strikeCall: number | null;
    // Campos Put Spread
    strikePutComprada: number | null;
    strikePutVendida: number | null;
    // Campos Put Vanilla
    strikePut: number | null;
    direcao: string | null;
  } | null {
    if (!this.operation) {
      return null;
    }

    return {
      // Campos comuns
      notional: this.operation.notionalAmount ?? 0,
      dataOperacao: this.operation.operationDate ?? null,
      dataVencimento: this.operation.dueAt ?? null,
      spot: this.operation.referenceExchangeRate ?? 0,
      // Campos NDF
      forward: this.operation.forwardRate ?? 0,
      cap: this.operation.capValue ?? 0,
      // Campos Call Spread
      strikeCallComprada: this.operation.strikeBuy ?? null,
      strikeCallVendida: this.operation.strikeSell ?? null,
      // Campos Call Vanilla
      strikeCall: this.operation.strikeBuy ?? null,
      // Campos Put Spread
      strikePutComprada: this.operation.strikeSell ?? null,
      strikePutVendida: this.operation.strikeBuy ?? null,
      // Campos Put Vanilla
      strikePut: this.operation.strikeBuy ?? null,
      direcao: this.translateDirection(this.operation.operationDirection),
    };
  }

  /**
   * Traduz a direção da operação
   */
  private translateDirection(direction: OperationDirectionType | undefined): string | null {
    if (!direction) {
      return null;
    }
    const translations: Record<OperationDirectionType, string> = {
      [OperationDirectionType.Buy]: 'Compra',
      [OperationDirectionType.Sell]: 'Venda',
    };
    return translations[direction] ?? direction;
  }

  constructor(
    private readonly mtmDailyService: DerivativeMtmDailyHttpService,
    private readonly derivativesService: DerivativesHttpService,
    private readonly toast: ToastHelperService,
    private readonly cdr: ChangeDetectorRef,
    private readonly riskMonitoringHttpService: RiskMonitoringHttpService,
    private readonly destroyRef: DestroyRef
  ) {}

  /**
   * Abre a modal e carrega os dados de MtM diário
   * @param derivativeOperationId ID da operação de derivativo
   */
  open(derivativeOperationId: string): void {
    this.derivativeOperationId = derivativeOperationId;
    this.data = [];
    this.sortedData = [];
    this.operation = null;
    this.mandateViolations = [];
    this.mandateResolved = null;

    this.dialog?.open('', '', '', 'Fechar', '', this.dialogKey, false, false);

    this.loadData();
  }

  close(): void {
    this.dialog?.close();
  }

  /**
   * Gera o MtM diário para a operação e recarrega os dados
   */
  generateMtmDaily(): void {
    if (!this.derivativeOperationId || this.isGenerating) {
      return;
    }

    this.isGenerating = true;

    this.mtmDailyService
      .generateMtmDaily(this.derivativeOperationId)
      .pipe(finalize(() => (this.isGenerating = false)))
      .subscribe({
        next: () => {
          this.toast.showToastSuccess('Sucesso', 'MtM diário gerado com sucesso');
          this.loadData();
        },
        error: () => {
          this.toast.showToastError('Erro', 'Não foi possível gerar o MtM diário');
          this.loadData();
        },
      });
  }

  /**
   * Carrega os dados da operação e do MtM diário
   */
  private loadData(): void {
    if (!this.derivativeOperationId) {
      return;
    }

    this.isLoading = true;

    forkJoin({
      operation: this.derivativesService.getById(this.derivativeOperationId),
      mtmDaily: this.mtmDailyService.getMtmDaily(this.derivativeOperationId),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ({ operation, mtmDaily }) => {
          this.operation = operation;
          this.data = mtmDaily || [];
          this.sortedData = [];
          if (operation) {
            this.evaluateMandateCompliance(operation);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.toast.showToastError('Erro', 'Não foi possível carregar os dados');
        },
      });
  }

  /**
   * Avalia a conformidade de mandato (dry-run, não-bloqueante) para o derivativo
   * carregado. Chamada 1x ao abrir a modal (detalhe, somente leitura; sem debounce).
   */
  private evaluateMandateCompliance(operation: DerivativeOperationDto): void {
    const companyId =
      operation.company?.id || operation.derivativeOperationDemand?.companyId || null;
    if (!companyId) {
      this.mandateResolved = null;
      this.mandateViolations = [];
      return;
    }

    const strategyType = operation.operationStrategyType || null;

    const payload: OperationComplianceEvaluateRequest = {
      companyId,
      currency: operation.derivativeOperationDemand?.internationalCurrency?.currencyCode ?? null,
      instrumentTypes: strategyType ? [strategyType] : null,
      capValue: operation.capValue ?? null,
      spot: operation.referenceExchangeRate ?? null,
    };

    this.riskMonitoringHttpService
      .evaluateMandateCompliance(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.mandateResolved = result?.mandateResolved ?? null;
        this.mandateViolations = result?.violations ?? [];
        this.cdr.markForCheck();
      });
  }

  /**
   * Retorna os itens para exibição (ordenados ou originais)
   */
  getItems(): DerivativeMtmDailyItemDto[] {
    if (this.sortedData.length > 0) {
      return this.sortedData;
    }
    return this.data;
  }

  /**
   * Handler para ordenação da tabela
   */
  handleTableSort(event: SortEvent): void {
    const { field, order } = event;

    if (!field || !order) {
      this.sortedData = [];
      return;
    }

    const isAscending = order === 1;
    const items = [...this.data];

    // Encontrar a definição da coluna para obter o campo correto para ordenação
    const columnDef = this.columnDefinitions.find(col => col.header === field);
    const sortField = columnDef?.sortField || columnDef?.field || field;

    this.sortedData = items.sort((a, b) => {
      const aValue = this.getFieldValue(a, sortField);
      const bValue = this.getFieldValue(b, sortField);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return isAscending
          ? aValue.localeCompare(bValue, 'pt-BR', { numeric: true })
          : bValue.localeCompare(aValue, 'pt-BR', { numeric: true });
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return isAscending ? aValue - bValue : bValue - aValue;
      }

      // Fallback para string
      const aStr = String(aValue);
      const bStr = String(bValue);
      return isAscending
        ? aStr.localeCompare(bStr, 'pt-BR', { numeric: true })
        : bStr.localeCompare(aStr, 'pt-BR', { numeric: true });
    });
  }

  /**
   * Acessa propriedades aninhadas usando notação de ponto
   */
  private getFieldValue(item: DerivativeMtmDailyItemDto, field: string): unknown {
    const keys = field.split('.');
    let result: unknown = item;

    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = (result as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }

    return result;
  }
}
